/**
 * Derives a two-stop CSS gradient from a solid base color so avatars render
 * with a subtle depth effect (lighter top-left → richer bottom-right) instead
 * of a flat fill. The base color is expected to come from
 * `getRandomColor({ string })`, keeping the result deterministic per user.
 *
 * Non-hex inputs (e.g. the "gray" fallback used for deleted users) are returned
 * as a flat gradient of themselves, so callers can pass any base color safely.
 */

type Rgb = { r: number; g: number; b: number };
type Hsl = { h: number; s: number; l: number };

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const parseHexColor = (color: string): Rgb | null => {
  const hex = color.trim().replace(/^#/, "");
  if (hex.length === 3) {
    const r = Number.parseInt(hex[0] + hex[0], 16);
    const g = Number.parseInt(hex[1] + hex[1], 16);
    const b = Number.parseInt(hex[2] + hex[2], 16);
    return { r, g, b };
  }
  if (hex.length === 6) {
    const r = Number.parseInt(hex.slice(0, 2), 16);
    const g = Number.parseInt(hex.slice(2, 4), 16);
    const b = Number.parseInt(hex.slice(4, 6), 16);
    if (![r, g, b].some(Number.isNaN)) {
      return { r, g, b };
    }
  }
  return null;
};

const rgbToHsl = ({ r, g, b }: Rgb): Hsl => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === rn) {
      h = ((gn - bn) / delta) % 6;
    } else if (max === gn) {
      h = (bn - rn) / delta + 2;
    } else {
      h = (rn - gn) / delta + 4;
    }
    h *= 60;
    if (h < 0) h += 360;
  }

  const l = (max + min) / 2;
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

  return { h, s: s * 100, l: l * 100 };
};

const hslToCss = ({ h, s, l }: Hsl): string =>
  `hsl(${Math.round(h)}, ${Math.round(s)}%, ${Math.round(l)}%)`;

export interface AvatarGradientStops {
  /** Top-left (lighter) stop. */
  light: string;
  /** Bottom-right (richer/darker) stop. */
  dark: string;
}

/**
 * The two color stops behind {@link getAvatarGradient}, exposed as discrete CSS
 * color strings so non-CSS renderers (SVG `<linearGradient>`, Konva
 * `fillLinearGradientColorStops`) can reproduce the same avatar gradient the
 * HTML avatar renders via `background-image`.
 */
export const getAvatarGradientStops = (
  baseColor: string
): AvatarGradientStops => {
  const rgb = parseHexColor(baseColor);
  if (!rgb) {
    return { light: baseColor, dark: baseColor };
  }

  const { h, s, l } = rgbToHsl(rgb);

  const light: Hsl = {
    h: (h + 6) % 360,
    s: clamp(s + 6, 0, 100),
    l: clamp(l + 14, 0, 92)
  };
  const dark: Hsl = {
    h: (h + 360 - 6) % 360,
    s: clamp(s + 20, 0, 100),
    l: clamp(l - 16, 8, 100)
  };

  return { light: hslToCss(light), dark: hslToCss(dark) };
};

export const getAvatarGradient = (baseColor: string): string => {
  const { light, dark } = getAvatarGradientStops(baseColor);
  return `linear-gradient(135deg, ${light}, ${dark})`;
};
